import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import TaxPlanningModal from "./TaxPlanningModal";
import {
  TAX_PLANNING_TITLE,
  buildPlaceholderEmployee,
  isTaxPlanningSubmitPayload,
  isDeclarationApprovalWorkflowRequired,
  getCurrentFinancialYear,
  normalizeFinancialYearKey,
  resolveEmployeeId,
  resolveTaxPlanningPayMonth,
} from "./taxPlanning";
import { employeeService, payrollService } from "../../../services/api";
import { getAuthItem, getAuthJson } from "../../../utils/authStorage";
import {
  RequestHistoryOverlay,
  useEssRequestHistory,
} from "../attendance/components/RequestHistoryOverlay";

const SETTINGS_TIMEOUT_MS = 4000;
const EMPLOYEE_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, fallback = null) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

function resolveContextEmployeeId(context) {
  const fromContext = resolveEmployeeId(context);
  if (fromContext) return fromContext;
  const currentUser = getAuthJson("currentUser");
  const raw =
    getAuthItem("employeeId") ||
    currentUser?.employeeId ||
    currentUser?.employee?.id ||
    null;
  const n = raw != null && raw !== "" ? Number(raw) : null;
  return Number.isFinite(n) ? n : null;
}

/**
 * Employee Request Types → Tax Planning.
 * Gates on Declaration settings (enabled + windows + tax year lock).
 */
export default function TaxPlanningQuickRequest({ context, onClose, onDeclared }) {
  const employeeId = resolveContextEmployeeId(context);
  const requestHistory = useEssRequestHistory({
    enabled: true,
    actionLabel: "Tax Planning",
    employeeId,
  });
  const [employee, setEmployee] = useState(() =>
    buildPlaceholderEmployee({ ...context, employeeId })
  );
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const loadGenRef = useRef(0);

  useEffect(() => {
    const gen = ++loadGenRef.current;
    let cancelled = false;
    const placeholder = buildPlaceholderEmployee({ ...context, employeeId });

    (async () => {
      setLoading(true);
      setLoadError("");
      setEmployee(placeholder);

      try {
        if (!employeeId) {
          if (!cancelled && gen === loadGenRef.current) {
            setLoadError(
              "No employee record is linked to your login. Ask HR/Payroll to link your user to an employee, or open Tax Planning from Payroll → Actions."
            );
            setLoading(false);
          }
          return;
        }

        const settingsResult = await withTimeout(
          payrollService.getPayrollSettings().catch((err) => {
            console.warn("Tax Planning: settings load failed", err);
            return null;
          }),
          SETTINGS_TIMEOUT_MS,
          null
        );

        if (cancelled || gen !== loadGenRef.current) return;

        const settings =
          settingsResult?.success && settingsResult.data
            ? settingsResult.data
            : settingsResult?.data || null;
        setPayrollSettings(settings);
        setLoading(false);

        const empResult = await withTimeout(
          employeeService.getEmployee(employeeId).catch((err) => {
            console.warn("Tax Planning: employee load failed", err);
            return null;
          }),
          EMPLOYEE_TIMEOUT_MS,
          null
        );

        if (cancelled || gen !== loadGenRef.current) return;

        const emp = empResult?.data ?? empResult?.employee ?? empResult;
        if (emp?.id || emp?.employeeId) {
          setEmployee({
            ...emp,
            id: emp.id ?? emp.employeeId ?? employeeId,
            employeeId: emp.employeeId ?? emp.id ?? employeeId,
          });
        } else {
          // Keep placeholder with known numeric id so saves still work
          setEmployee((prev) => ({
            ...prev,
            id: employeeId,
            employeeId,
          }));
        }
      } catch (err) {
        console.error("Tax Planning quick request load error", err);
        if (!cancelled && gen === loadGenRef.current) {
          setLoadError(
            "Could not load Tax Planning. You can close and retry."
          );
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  if (loading) {
    const loadingNode = (
      <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-8 text-center shadow-xl">
          <p className="text-sm text-gray-700">Loading {TAX_PLANNING_TITLE}…</p>
          <p className="mt-2 text-xs text-gray-500">Checking Declaration settings…</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 rounded-full border border-gray-300 px-4 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
    return typeof document !== "undefined"
      ? createPortal(loadingNode, document.body)
      : loadingNode;
  }

  if (loadError) {
    const errNode = (
      <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{TAX_PLANNING_TITLE}</h3>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-4 p-4">
            <p className="text-sm text-gray-700">{loadError}</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-gray-300 bg-white px-4 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
    return typeof document !== "undefined"
      ? createPortal(errNode, document.body)
      : errNode;
  }

  const declarationEnabled = !!payrollSettings?.isInvestmentEnabled;
  const proofEnabled = !!payrollSettings?.isProofEnabled;
  const fy = payrollSettings?.taxYear || getCurrentFinancialYear();
  const planningPayMonth = resolveTaxPlanningPayMonth({ financialYear: fy });
  const fyKey = normalizeFinancialYearKey(fy);
  const lockedList = Array.isArray(payrollSettings?.lockedTaxYears)
    ? payrollSettings.lockedTaxYears.map(normalizeFinancialYearKey)
    : [];
  const taxYearLocked =
    lockedList.includes(fyKey) ||
    (!!payrollSettings?.isTaxYearLocked &&
      (!payrollSettings?.taxYear ||
        normalizeFinancialYearKey(payrollSettings.taxYear) === fyKey));

  // Employee: Declaration OFF / settings missing / tax year locked → view only (no edit chrome).
  const employeeMayEdit = !!payrollSettings && declarationEnabled && !taxYearLocked;
  const modalMode = employeeMayEdit ? "edit" : "view";

  const gateBlocked = !employeeMayEdit;
  const gateMessage = !payrollSettings
    ? "Payroll Declaration settings could not be loaded. Tax Planning is view-only. Contact HR/Payroll."
    : !declarationEnabled
    ? "Declaration is turned off in Payroll Settings. You can view Tax Planning, but edits are blocked until HR enables Declaration."
    : taxYearLocked
    ? `Tax year ${fy} is locked. You cannot edit Tax Planning until Payroll unlocks it.`
    : "";

  return (
    <>
      {gateBlocked && gateMessage && typeof document !== "undefined"
        ? createPortal(
            <div className="pointer-events-none fixed bottom-4 left-1/2 z-[10055] w-[min(92vw,28rem)] -translate-x-1/2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900 shadow-lg">
              {gateMessage}
            </div>,
            document.body
          )
        : null}
      <TaxPlanningModal
        employee={employee}
        mode={modalMode}
        onClose={onClose}
        historySlot={
          <button
            type="button"
            onClick={() => requestHistory.setOpen(true)}
            className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-300 bg-slate-100 px-4 text-xs font-medium text-slate-800 hover:bg-slate-200"
          >
            View history
            {!requestHistory.loading && requestHistory.rows.length > 0
              ? ` (${requestHistory.rows.length})`
              : ""}
          </button>
        }
        onSave={(updatedData) => {
          if (!employeeMayEdit) return;
          if (!isTaxPlanningSubmitPayload(updatedData)) return;
          const needsApproval = isDeclarationApprovalWorkflowRequired(payrollSettings);
          onDeclared?.({
            ...(updatedData.formData || {}),
            financialYear: updatedData.financialYear || fy,
            taxRegime: updatedData.taxRegime === "old" ? "OLD" : "NEW",
            _needsApproval: needsApproval,
          });
        }}
        payrollSettings={payrollSettings}
        isInvestmentDeclarationEnabled={declarationEnabled}
        isInvestmentProofEnabled={proofEnabled}
        financialYear={fy}
        payMonth={planningPayMonth}
        adminBypassWindows={false}
      />
      <RequestHistoryOverlay
        open={requestHistory.open}
        onClose={() => requestHistory.setOpen(false)}
        title="Tax Planning application history"
        subtitle={employee?.name || context?.userName || "Employee"}
        rows={requestHistory.rows}
        loading={requestHistory.loading}
      />
    </>
  );
}
